import { useCallback } from 'react'
import type { ReactNode } from 'react'
import { useMeetingSessionStore } from '@/features/meeting/store/meeting-session.store'
import { deviceManager } from '@/features/devices/services/device-manager'
import { MediaStreamContext } from './media-stream-context'

/**
 * The local camera, for tiles that have no published stream to render.
 *
 * This used to hold its OWN capture behind its own refcount — a second camera
 * open for the whole call, at hardcoded 1280x720@30 regardless of the quality
 * preset the meeting was publishing at. Someone on "Low" ran a 320x180 capture
 * for the room and a 720p one to draw their own thumbnail. It also broke things
 * elsewhere: the background dialog previewing through here meant two MediaPipe
 * runs on one camera, which corrupted the published mask, and closing the
 * dialog could drop this refcount to zero and black out the local tile.
 *
 * It is now a thin adapter over `deviceManager` — the same shared camera track
 * everything else uses, wrapped in a MediaStream because that is what a
 * `<video>` element wants. The provider keeps its shape so tiles and previews
 * are unchanged; only the ownership underneath it moved.
 */
export function MediaStreamProvider({ children }: { children: ReactNode }) {
  const getLocalStream = useCallback(async () => {
    // A view-only attendee never publishes, so there is nothing to open.
    //
    // Refused here rather than at each call site because this is the route a
    // tile takes to the camera. Asking anyway would put a browser permission
    // prompt in front of someone whose camera this meeting will never use, and
    // a refusal there is remembered by the browser for the whole origin,
    // breaking the next meeting they join as a full participant.
    //
    // An empty MediaStream rather than a rejection: every caller already
    // handles a stream with no video track by falling back to the avatar
    // placeholder, whereas a throw would surface as a device error nobody can
    // act on.
    if (useMeetingSessionStore.getState().viewOnly) {
      return new MediaStream()
    }

    const track = await deviceManager.acquire('video')
    return track ? new MediaStream([track]) : new MediaStream()
  }, [])

  const releaseLocalStream = useCallback(() => {
    if (useMeetingSessionStore.getState().viewOnly) return
    deviceManager.release('video')
  }, [])

  // Kept for the existing callers. The manager owns the real count now, and it
  // spans the lobby and the meeting rather than just the tiles.
  const getStreamRefCount = useCallback(() => (deviceManager.getTrack('video') ? 1 : 0), [])

  // `getLocalStream` wraps the track that was live at the moment of asking.
  // Switching cameras stops that track and opens a new one, so a tile holding
  // only the first answer rendered an ended track — blank for you while the
  // room, which follows the producer, saw the new camera fine.
  const subscribeLocalStream = useCallback(
    (listener: (stream: MediaStream | null) => void) => {
      if (useMeetingSessionStore.getState().viewOnly) return () => {}
      return deviceManager.subscribe('video', (track) => {
        listener(track ? new MediaStream([track]) : null)
      })
    },
    [],
  )

  return (
    <MediaStreamContext.Provider
      value={{ getLocalStream, releaseLocalStream, getStreamRefCount, subscribeLocalStream }}
    >
      {children}
    </MediaStreamContext.Provider>
  )
}

